import * as rax from "retry-axios";
import axios from "axios";

import store from "@/store";
import { getToken } from "@/utils/auth";
import { getDomainSite, removeDomainSite } from "./site";
import { log2 } from "./log2";
import siteState from "../store/modules/site";
import { message } from "ant-design-vue";
import { i18n } from "../language";
import { refreshTokenIfNeeded } from "./tokenRefresh";
import { isSessionInvalid, markSessionInvalid, shouldDispatchLogout } from "./authGuard";

const log = log2("request.js");

let apiAddress = process.env.TABLET_URL;

let site = {
    apiAddress: apiAddress,
    _id: "",
};
try {
    const invalidateCache = window.location.href.indexOf("forceResetPassword") !== -1;
    if (invalidateCache) {
        console.info("invalidating all storage");
        localStorage.clear();
        const cookies = document.cookie.split(";");

        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
        }
    }
} catch (e) {}

log("axios process.env", {
    env: process.env,
    apiAddress: apiAddress,
});
/*removeDomainSite();*/
if (getDomainSite()) {
    site = JSON.parse(getDomainSite());
    log("parsed site data", { ...site });
    site.apiAddress = apiAddress;
}

// create an axios instance
const service = axios.create({
    baseURL: apiAddress,
    timeout: 20000, // request timeout
    raxConfig: {
        // instance: service,
        retry: 3,
        backoffType: "exponential",
        httpMethodsToRetry: ["GET"],
        shouldRetry: (axiosError) => {
            if (isSessionInvalid()) return false;
            var status = axiosError.response && axiosError.response.status;
            if (status === 401 || status === 403) return false;
            const cfg = rax.getConfig(axiosError);
            if (cfg.currentRetryAttempt >= cfg.retry) return false;
            const url = axiosError.config.url || "";
            const retryEnabledPaths = [
                "v2-message-history",
                "tags",
                "users/pagination",
                "conversations/history",
                "billing/get-billing-detail",
                "contactProperty",
                "channel/list",
                "chatbot/user-segments/listSegments",
                "messages?all=false",
                "settings/get-all",
                "preparedContents/pagination",
                "visitors/",
                "last-note",
            ];
            var shouldRetry = retryEnabledPaths.some((path) => url.indexOf(path) !== -1);

            console.log("shouldRetry", url, cfg);

            return shouldRetry;
        },
        onRetryAttempt: (err) => {
            const cfg = rax.getConfig(err);
            console.log(`Retry attempt #${cfg?.currentRetryAttempt}`);
        },
    },
});
service.defaults.raxConfig = {
    instance: service,
};

const interceptorId = rax.attach(service);

// URLs that should skip token refresh check
const skipRefreshUrls = ["/sessions/refresh", "/sessions/validate", "/login", "/passwordless", "/email-validation/accept-invitation"];

const authGuardWhitelistUrls = [
    "/users/login",
    "/users/passwordless-login",
    "/users/passwordless-login-verify",
    "/users/forgot-password",
    "/users/reset-password",
    "/users/email-validation/accept-invitation",
    "/sessions/refresh",
    "/sessions/validate",
    "/site/",
    "/sites/",
];

// request interceptor
service.interceptors.request.use(
    async (config) => {
        const url = config.url || "";
        const isWhitelisted = authGuardWhitelistUrls.some(function (path) { return url.indexOf(path) !== -1; });
        if (isSessionInvalid() && !isWhitelisted) {
            return Promise.reject(new axios.Cancel("Session invalid (401)"));
        }

        const shouldSkipRefresh = skipRefreshUrls.some((path) => url.includes(path));
        
        // Try to refresh token if needed (won't block if not needed)
        if (!shouldSkipRefresh && store.state.auth.token) {
            try {
                await refreshTokenIfNeeded();
            } catch (e) {
                // Ignore refresh errors, continue with request
            }
        }
        
        config.headers["authorization"] = `Bearer ${getToken()}`;
        const siteId = site._id || (siteState.state.site ? siteState.state.site._id : undefined);
        const user = store.state.auth.user;
        const adminUser = store.state.auth.adminUser;
        const isLoggedIn = !!store.state.auth.token;
        if (siteId) {
            config.headers["site-id"] = siteId;
        }
        if (isLoggedIn) {
            if (user?._id) config.headers["x-actor-user-id"] = user?._id;
            if (adminUser?._id) {
                config.headers["x-actor-admin-user-id"] = adminUser?._id;
                config.headers["x-powered-by-super-admin"] = true;
            }
        }
        const sessionId = sessionStorage.getItem("sessionId");
        if (sessionId) config.headers["session-id"] = sessionId;
        config._url = config.url;
        return config;
    },
    (error) => {
        Promise.reject(error);
    }
);

// response interceptor
service.interceptors.response.use(
    (response) => response,
    (error) => {
        const pTokenExists =
            window.location.href.indexOf("passwordlessLoginToken") !== -1 &&
            window.location.href.indexOf("ptoken") !== -1;
        if (error.response && error.response.status && error.response.status === 401 && !pTokenExists) {
            var reqUrl = (error.config && (error.config._url || error.config.url)) || "";
            markSessionInvalid("axios.response.401", { url: reqUrl });
            if (shouldDispatchLogout("axios.response.401", { url: reqUrl })) {
                store.dispatch("auth/logout");
            }
        }

        return Promise.reject(error);
    }
);

service.interceptors.response.use(
    function (response) {
        return response;
    },
    function (error) {
       
        
        if (error?.response?.status === 403) {
            var data = error.response.data || {};
            if (
                [
                    "EMAIL_VALIDATION_REQUIRED",
                    "AUTH_FAILED_INVALID_CREDENTIALS",
                    "USER_EMAIL_UPDATE_NOT_ALLOWED",
                    "OWNER_EMAIL_UPDATE_NOT_ALLOWED",
                    "ONLY_OWNER_CAN_UPDATE_USER_EMAIL",
                ].includes(data.errorCode||data.code) &&
                data.localizedMessages
            ) {
                message.error(data.localizedMessages[i18n.locale] || data.localizedMessages.tr);
                return Promise.reject(error);
            }
            console.log("request debug",{
                status: error?.response?.status,
                code: error?.response?.data?.code,
                errorcode: error?.response?.data?.errorcode,
                errorData: error?.data,
                data,
                if1:data.localizedMessages,
                if2: [
                    "EMAIL_VALIDATION_REQUIRED",
                    "USER_EMAIL_UPDATE_NOT_ALLOWED",
                    "OWNER_EMAIL_UPDATE_NOT_ALLOWED",
                    "ONLY_OWNER_CAN_UPDATE_USER_EMAIL",
                ].includes(data.errorcode||data.code)
            });
            
            return message.error(i18n.t("message.notAuthorizedToPerformOperation"));
        }
        return Promise.reject(error);
    }
);

export default service;
